'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, useState } from 'react';
import { cx } from '../../../internal/cx.js';
import { Avatar } from '../../../components/identity/avatar/index.js';
import { Button } from '../../../components/actions/button/index.js';
import { RelativeTime, } from '../../../components/data-display/relative-time/index.js';
import { DescriptionItem, DescriptionList, } from '../../../components/data-display/description-list/index.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '../../../components/data-grid/data-table/index.js';
import { LoadMore } from '../../../components/data-grid/load-more/index.js';
import { Skeleton } from '../../../components/feedback/skeleton/index.js';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, } from '../../../components/overlays/sheet/index.js';
const DEFAULT_MESSAGES = {
    caption: 'Audit log',
    time: 'Time',
    actor: 'Actor',
    action: 'Action',
    target: 'Target',
    origin: 'IP and device',
    details: 'Details',
    export: 'Export CSV',
    exporting: 'Exporting…',
    ipAddress: 'IP address',
    device: 'Device',
    location: 'Location',
    source: 'Source',
    reason: 'Reason',
    changes: 'Changes',
    field: 'Field',
    before: 'Before',
    after: 'After',
    none: 'None',
    automation: 'Automation',
    system: 'System',
    api: 'API',
    unknownActor: 'Unknown',
    empty: 'No events match',
    loading: 'Loading audit events',
    close: 'Close',
};
const ALL_COLUMNS = ['time', 'actor', 'action', 'target', 'origin'];
function toDate(input) {
    return input instanceof Date ? input : new Date(input);
}
function iso(input) {
    const date = toDate(input);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
function absolute(input, locale, timeZone) {
    const date = toDate(input);
    if (Number.isNaN(date.getTime()))
        return '—';
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'medium',
        ...(timeZone ? { timeZone } : {}),
    }).format(date);
}
/**
 * Builds a CSV file (RFC 4180) from rows of cells, for an audit log export.
 * Quotes cells that need it and defuses spreadsheet formulas (a cell that
 * starts with `=`, `+`, `-`, `@`, tab or carriage return is prefixed with `'`).
 */
export function auditLogToCsv(rows) {
    const cell = (value) => {
        let text = value === null || value === undefined
            ? ''
            : value instanceof Date
                ? value.toISOString()
                : String(value);
        if (/^[=+\-@\t\r]/.test(text))
            text = `'${text}`;
        return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    return rows.map((row) => row.map(cell).join(',')).join('\r\n');
}
/**
 * Security and audit events in a table: time, actor, action, target, IP and
 * device, with a details drawer (field changes, reason, payload), a filters
 * slot, an export action and cursor pagination.
 */
export const AuditLogViewer = forwardRef(function AuditLogViewer({ events, filters, onExport, exporting = false, columns = ALL_COLUMNS, selectedId: selectedProp, defaultSelectedId = null, onSelectedChange, renderDetails, hasMore = false, loadingMore = false, loadMoreError = false, onLoadMore, loading = false, error, empty, density = 'comfortable', now, locale, timeZone, messages: messagesProp, className, ...rest }, ref) {
    const m = { ...DEFAULT_MESSAGES, ...messagesProp };
    const id = useId();
    const [selectedInner, setSelectedInner] = useState(defaultSelectedId);
    const selectedId = selectedProp !== undefined ? selectedProp : selectedInner;
    const select = (next) => {
        if (selectedProp === undefined)
            setSelectedInner(next);
        onSelectedChange?.(next);
    };
    const selected = selectedId ? events.find((e) => e.id === selectedId) : undefined;
    const safe = (v) => v.replace(/[^\w-]/g, '_');
    const detailsButtonId = (eventId) => `${id}-details-${safe(eventId)}`;
    const actionTextId = (eventId) => `${id}-action-${safe(eventId)}`;
    const timeProps = {
        ...(now !== undefined ? { now } : {}),
        ...(locale !== undefined ? { locale } : {}),
        ...(timeZone !== undefined ? { timeZone } : {}),
    };
    const kindLabel = (kind) => kind === 'automation'
        ? m.automation
        : kind === 'system'
            ? m.system
            : kind === 'api'
                ? m.api
                : null;
    const actorCell = (actor) => {
        if (!actor)
            return _jsx("span", { className: "mn-audit-log-viewer__muted", children: m.unknownActor });
        const tag = kindLabel(actor.kind);
        return (_jsxs("span", { className: "mn-audit-log-viewer__actor", children: [_jsx(Avatar, { name: actor.name, src: actor.avatarSrc ?? null, size: "xs", decorative: true, ...(actor.seed !== undefined ? { seed: actor.seed } : {}) }), _jsx("span", { className: "mn-audit-log-viewer__actor-name", children: actor.name }), tag ? _jsx("span", { className: "mn-audit-log-viewer__tag", children: tag }) : null] }));
    };
    const header = {
        time: m.time,
        actor: m.actor,
        action: m.action,
        target: m.target,
        origin: m.origin,
    };
    const cell = (column, event) => {
        switch (column) {
            case 'time':
                return (_jsx("span", { className: "mn-audit-log-viewer__time", children: _jsx("time", { dateTime: iso(event.time), suppressHydrationWarning: true, children: absolute(event.time, locale, timeZone) }) }));
            case 'actor':
                return actorCell(event.actor);
            case 'action':
                return (_jsxs("span", { className: "mn-audit-log-viewer__action", children: [_jsx("span", { id: actionTextId(event.id), children: event.action }), event.source ? (_jsx("span", { className: "mn-audit-log-viewer__source", children: event.source })) : null, columns.includes('time') ? (
                        // Shown only in narrow containers, where the Time column is hidden.
                        _jsx("time", { className: "mn-audit-log-viewer__time-inline", dateTime: iso(event.time), suppressHydrationWarning: true, children: absolute(event.time, locale, timeZone) })) : null] }));
            case 'target':
                return event.target ?? _jsx("span", { className: "mn-audit-log-viewer__muted", children: "\u2014" });
            case 'origin':
                return event.ip || event.device ? (_jsxs("span", { className: "mn-audit-log-viewer__origin", children: [event.ip ? _jsx("span", { className: "mn-audit-log-viewer__ip", children: event.ip }) : null, event.device ? (_jsx("span", { className: "mn-audit-log-viewer__device", children: event.device })) : null] })) : (_jsx("span", { className: "mn-audit-log-viewer__muted", children: "\u2014" }));
        }
    };
    let body;
    if (loading) {
        body = (_jsx("div", { className: "mn-audit-log-viewer__skeleton", role: "img", "aria-label": m.loading, children: [0, 1, 2, 3].map((i) => (_jsx(Skeleton, { shape: "rect", height: "2.25rem" }, i))) }));
    }
    else if (error) {
        body = _jsx("div", { className: "mn-audit-log-viewer__error", children: error });
    }
    else if (events.length === 0) {
        body = _jsx("div", { className: "mn-audit-log-viewer__empty", children: empty ?? m.empty });
    }
    else {
        body = (
        // aria-label, not a visually hidden caption: an absolutely positioned caption escapes
        // the table's scroll wrapper and widens the page on phones.
        _jsxs(Table, { density: density, className: "mn-audit-log-viewer__table", "aria-label": m.caption, children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [columns.map((c) => (_jsx(TableHead, { scope: "col", "data-column": c, children: header[c] }, c))), _jsx(TableHead, { scope: "col", "data-column": "details", children: _jsx("span", { className: "mn-visually-hidden", children: m.details }) })] }) }), _jsx(TableBody, { children: events.map((event) => {
                        const actionId = actionTextId(event.id);
                        const buttonId = detailsButtonId(event.id);
                        return (_jsxs(TableRow, { selected: event.id === selectedId, children: [columns.map((c) => (_jsx(TableCell, { "data-column": c, children: cell(c, event) }, c))), _jsx(TableCell, { "data-column": "details", children: _jsx(Button, { id: buttonId, size: "xs", variant: "ghost", "aria-haspopup": "dialog", "aria-labelledby": columns.includes('action') ? `${buttonId} ${actionId}` : undefined, onClick: () => select(event.id), children: m.details }) })] }, event.id));
                    }) })] }));
    }
    const changes = selected?.changes ?? [];
    const value = (v) => v === undefined || v === null || v === '' ? (_jsx("span", { className: "mn-audit-log-viewer__muted", children: m.none })) : (v);
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-audit-log-viewer', className), children: [filters || onExport ? (_jsxs("div", { className: "mn-audit-log-viewer__toolbar", children: [_jsx("div", { className: "mn-audit-log-viewer__filters", children: filters }), onExport ? (_jsx(Button, { size: "sm", loading: exporting, onClick: onExport, children: exporting ? m.exporting : m.export })) : null] })) : null, body, hasMore && onLoadMore && !loading && !error ? (_jsx(LoadMore, { hasMore: true, loading: loadingMore, error: loadMoreError, onLoadMore: onLoadMore, loadedCount: events.length, noun: { one: 'event', other: 'events' } })) : null, _jsx(Sheet, { open: selected !== undefined, onOpenChange: (open) => (open ? null : select(null)), children: selected ? (_jsxs(SheetContent, { size: "md", closeLabel: m.close, className: "mn-audit-log-viewer__sheet", 
                    // Return focus to the row's Details button, also when the product opened the drawer.
                    onCloseAutoFocus: (event) => {
                        const button = document.getElementById(detailsButtonId(selected.id));
                        if (button) {
                            event.preventDefault();
                            button.focus();
                        }
                    }, children: [_jsxs(SheetHeader, { children: [_jsx(SheetTitle, { children: selected.action }), _jsxs(SheetDescription, { children: [_jsx("time", { dateTime: iso(selected.time), children: absolute(selected.time, locale, timeZone) }), ' · ', _jsx(RelativeTime, { date: selected.time, tooltip: false, ...timeProps })] })] }), _jsxs(SheetBody, { children: [_jsxs(DescriptionList, { columns: 1, layout: "inline", children: [_jsx(DescriptionItem, { term: m.actor, children: _jsxs("span", { className: "mn-audit-log-viewer__stack", children: [actorCell(selected.actor), selected.actor?.detail ? (_jsx("span", { className: "mn-audit-log-viewer__muted", children: selected.actor.detail })) : null] }) }), selected.target ? (_jsx(DescriptionItem, { term: m.target, children: selected.target })) : null, selected.ip ? (_jsx(DescriptionItem, { term: m.ipAddress, children: _jsx("span", { className: "mn-audit-log-viewer__ip", children: selected.ip }) })) : null, selected.device ? (_jsx(DescriptionItem, { term: m.device, children: selected.device })) : null, selected.location ? (_jsx(DescriptionItem, { term: m.location, children: selected.location })) : null, selected.source ? (_jsx(DescriptionItem, { term: m.source, children: selected.source })) : null, selected.reason ? (_jsx(DescriptionItem, { term: m.reason, children: selected.reason })) : null] }), changes.length > 0 ? (_jsxs("div", { className: "mn-audit-log-viewer__changes", children: [_jsx("h3", { className: "mn-audit-log-viewer__section-title", id: `${id}-changes`, children: m.changes }), _jsxs(Table, { density: "compact", "aria-labelledby": `${id}-changes`, children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { scope: "col", children: m.field }), _jsx(TableHead, { scope: "col", children: m.before }), _jsx(TableHead, { scope: "col", children: m.after })] }) }), _jsx(TableBody, { children: changes.map((c, i) => (_jsxs(TableRow, { children: [_jsx(TableHead, { scope: "row", className: "mn-audit-log-viewer__field", children: c.field }), _jsx(TableCell, { "data-change": "before", children: value(c.before) }), _jsx(TableCell, { "data-change": "after", children: value(c.after) })] }, i))) })] })] })) : null, selected.details ? (_jsx("div", { className: "mn-audit-log-viewer__details", children: selected.details })) : null, renderDetails ? renderDetails(selected) : null] })] })) : null })] }));
});
//# sourceMappingURL=audit-log-viewer.js.map